import * as React from "react";
import { PADebug } from "./PADebug"
import { ArticleGW } from "./ArticleGW"
import { BDKalkCore } from "./BDKalkCore"

/* << SearchCall >>
   Whenever a search is set to begin, a SearchCall is created to hold all the information needed to
   perform the search. Because of this, it will be possible to perform just fractions of the search at a time
   and in between those fractions let the rest of the system run their tasks. The idea here is to allow
   for new search calls to interrupt any ongoing searches so that they won't consume any more computing power,
   since only the latest search is being used anyhow. The effect is that letters and words typed into the
   search field will result in more immediate search responses. */

const searchedArticlesPerSequence = 2000
const sequenceDelay = 0.4

export class SearchCall {

    constructor(input:string, withinArticles: Array<ArticleGW>, searchIndex: number, indexCheckpoint: () => number, resultHandler: (result: Array<ArticleGW>) => void, searchFlags: number, core: BDKalkCore) {
        this.m_searchIndex = searchIndex
        this.m_targetArticles = withinArticles
        this.m_returnedCall = resultHandler
        this.m_iterator = 0
        this.m_resultingArticles = new Array<ArticleGW>()
        this.m_indexCheckpoint = indexCheckpoint
        this.m_active = true
        this.m_flags = searchFlags
        this.m_core = core
        this.m_wordIterator = 0
        this.m_words = new Array<string>()
        this.m_input = input
        
        PADebug.Log("SearchCall.constructor - created search for: \"" + input + "\" , index:" + this.m_searchIndex)
    }

    GetPickedWord() {
        if (this.m_wordIterator < this.m_words.length) {
            return this.m_words[this.m_wordIterator]
        }
        else {
            return null
        }
    }

    GetIndex() {
        return this.m_searchIndex
    }

    BeginSearch() {
        PADebug.Log("  SearchCall.BeginSearch() - starting search for index:" + this.m_searchIndex)

        const input = this.m_input
        var isEmptyString = false

        if (input == "") {
            isEmptyString = true
        }
        else {
            var newWord = ""
            for (var i = 0; i < input.length; i++) {
                if (input.charAt(i) == " ") {
                    if (newWord.length > 0) {
                        this.m_words.push(newWord)
                        newWord = ""
                        continue
                    }
                    else {
                        continue
                    }
                }
                else {
                    newWord += input.charAt(i)
                }
            }

            if (newWord != "") {
                this.m_words.push(newWord)
            }

            if (this.m_words.length == 0) {
                // The user seem to have typed in some blank spaces, which counts as nothing and will return the target array unaffected.
                isEmptyString = true
            }
        }

        if (isEmptyString == true) { // code -5 means the input was an empty string, the target articles will be returned as is.
            if (this.m_targetArticles) {
                this.ReturnResult(this.m_targetArticles)
                this.CanselSearch()
                return
            }
            else {
                PADebug.Assert(false, "Searchcall.BeginSearch - m_targetArticles was null when supposed to be a valid array!")
                this.ReturnResult(new Array<ArticleGW>())
            }
        }
        
        PADebug.Assert(this.m_iterator == 0, "SearchCall.BeginSearch() - This search call was already begun, it should not be run more than once!")

        this.PerformNextSequence()
    }

    PerformNextSequence() {
        var targLength = "null"
        if (this.m_targetArticles) {
            targLength = this.m_targetArticles.length.toString()
        }
        var resultLength = "null"
        if (this.m_resultingArticles) {
            resultLength = this.m_resultingArticles.length.toString()
        }

        PADebug.Log("   --- SearchCall.PerformNextSequence() - index:" + this.m_searchIndex.toString() + " word:\"" + this.GetPickedWord() + "\" wordNo:" + this.m_wordIterator.toString() + " iteration:" + this.m_iterator.toString() + " targetLength:" + targLength + " resultLength:" + resultLength)
        const currentSearch = this.m_indexCheckpoint()

        if (this.m_searchIndex != currentSearch) {
            // This is no longer the most actual search, aborting mission.
            this.CanselSearch()
            return
        }

        var targets = this.m_targetArticles
        var results = this.m_resultingArticles

        if ((targets == null) || (results == null)) {
            return
        }

        // Performing a sequence of search checks..
        for (var i = this.m_iterator, c = 0; (c < searchedArticlesPerSequence) && (i < targets.length); i++ , c++) {
            const word = this.GetPickedWord()

            if (word) {
                if (this.m_core.Match(targets[i], word, this.m_searchIndex) == true) {
                    results.push(targets[i])
                }
            }
            else {
                PADebug.Assert(false, "SearchCall.PerformNextSequence - tested word was null when expected to be valid")
            }
        }

        if ((this.m_iterator + searchedArticlesPerSequence) < targets.length) {
            // The thread will come back as soon as any other queued events have been carried out
            this.m_iterator += searchedArticlesPerSequence
            PADebug.Log("   --- SearchCall.PerformNextSequence() - releasing thread after completed search sequence. searchCall index:" + this.m_searchIndex.toString())
            setTimeout(this.PerformNextSequence.bind(this), sequenceDelay) 
        }
        else {
            // The search has reached its end for this word and should now apply the next one
            this.m_wordIterator++
            const triedWord = this.GetPickedWord()
            if (triedWord) {
                this.m_targetArticles = this.m_resultingArticles
                this.m_iterator = 0

                if (this.m_targetArticles) {
                    if (this.m_targetArticles.length > 0) {
                        this.m_resultingArticles = new Array<ArticleGW>()
                        setTimeout(this.PerformNextSequence.bind(this), sequenceDelay)
                    }
                    else {
                        // It looks like there are no resulting articles left, so the process returns an empty array
                        this.ReturnResult(this.m_targetArticles)
                        this.CanselSearch()
                        return
                    }
                }

                PADebug.Assert(false, "SearchCall.PerformNextSequence - m_resultingArticles was null between the word sequences")
            }
            else {
                // There are no more words to apply, so the search should return its result and finish
                var result = this.m_resultingArticles
                if (result) {
                    this.ReturnResult(result)
                }

                // The result has been returned, so this searchCall can be shut down
                this.CanselSearch() 
            }
        }
    }

    CanselSearch() {
        this.m_resultingArticles = null
        this.m_targetArticles = null
        this.m_active = false
        this.m_iterator = 999999999999
        this.m_wordIterator = 999999999999
    }

    IsCurrent() {
        const currentSearch = this.m_indexCheckpoint()

        if (this.m_searchIndex == currentSearch) {
            return true
        }

        return false
    }

    IsActive() {
        return this.m_active
    }

    ReturnResult(result: Array<ArticleGW>) {
        if (this.IsCurrent() == true) {
            this.m_returnedCall(result)
        }
    }
    
    private m_returnedCall: (result: Array<ArticleGW>) => void
    private m_indexCheckpoint: () => number
    private m_targetArticles: Array<ArticleGW> | null
    private m_resultingArticles: Array<ArticleGW> | null
    private m_searchIndex: number
    private m_iterator: number
    private m_active: boolean
    private m_flags: number
    private m_core: BDKalkCore
    private m_words: Array<string>
    private m_wordIterator: number
    private m_input : string
}




// WEBPACK FOOTER //
// ./client/view/SearchCall.tsx